import React, { useEffect, useState } from "react";
import { fetchChouxboxFileGroups } from "./api";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

// Step 1's second source: pick one of the selected restaurant's unencoded
// Chouxbox file groups instead of uploading a file. Loads on demand (button),
// never on page load. onImport(fileGroup) does the actual import.
const ChouxboxFileGroupPicker = ({ account, location, disabled, importingId, onImport, onPreview }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const locationId = location ? location.id : null;
  useEffect(() => {
    setData(null);
    setError(null);
  }, [locationId]);

  if (!account) return <div className="small text-muted">Pick a target account and location above first.</div>;
  if (account.platform !== "chouxbox") {
    return <div className="small text-muted">Chouxbox file groups are only available for Chouxbox accounts.</div>;
  }
  if (!location) return <div className="small text-muted">Pick a location above first -- its Chouxbox restaurant's uploads are listed here.</div>;

  const load = (page = 1) => {
    setLoading(true);
    setError(null);
    fetchChouxboxFileGroups(account.id, location.id, page)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total_count / data.per_page)) : 1;

  return (
    <div>
      <div className="d-flex align-items-center mb-2" style={{ gap: "8px" }}>
        <button
          type="button"
          className={`btn btn-sm ${data ? "btn-outline-secondary" : "btn-outline-primary"} py-0 px-2`}
          style={{ fontSize: "11px" }}
          disabled={loading}
          onClick={() => load(data ? data.page : 1)}
        >
          {loading ? "Loading..." : data ? "Refresh" : "Load file groups"}
        </button>
        {data && (
          <span className="small text-muted">
            {data.total_count} unencoded upload{data.total_count === 1 ? "" : "s"} at {location.name}
          </span>
        )}
      </div>

      {error && <div className="alert alert-danger py-1 px-2 small">{error}</div>}

      {data && data.file_groups.length === 0 && <div className="small text-muted">No unencoded uploads at this restaurant.</div>}

      {data && data.file_groups.length > 0 && (
        <>
          <div style={{ maxHeight: "300px", overflowY: "auto" }}>
            <table className="table table-sm small mb-1">
              <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                <tr>
                  <th>Uploaded</th>
                  <th>File</th>
                  <th className="text-end">Pages</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.file_groups.map((group) => {
                  const previewUrl = group.pdf_url || group.page_urls[0];
                  return (
                    <tr key={group.id}>
                      <td className="text-nowrap">{formatDate(group.uploaded_at)}</td>
                      <td style={{ wordBreak: "break-word" }}>
                        {group.file_name || `File group ${group.id}`}
                        <span className={`badge ms-1 ${group.source === "pdf" ? "bg-info text-dark" : "bg-secondary"}`} style={{ fontSize: "10px" }}>
                          {group.source === "pdf" ? "PDF" : "images"}
                        </span>
                        {group.notes && <div className="text-muted">{group.notes}</div>}
                      </td>
                      <td className="text-end">{group.page_count}</td>
                      <td className="text-end text-nowrap">
                        {previewUrl && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 me-2"
                            style={{ fontSize: "11px" }}
                            onClick={() => onPreview({ url: previewUrl, title: group.file_name || `File group ${group.id}` })}
                          >
                            Preview
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm py-0 px-2"
                          style={{ fontSize: "11px" }}
                          disabled={disabled || importingId !== null}
                          onClick={() => onImport(group)}
                        >
                          {importingId === group.id ? "Importing..." : "Use this"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="d-flex align-items-center justify-content-end small" style={{ gap: "8px" }}>
              <button type="button" className="btn btn-link btn-sm p-0" disabled={loading || data.page <= 1} onClick={() => load(data.page - 1)}>
                ‹ Newer
              </button>
              <span className="text-muted">
                Page {data.page} of {totalPages}
              </span>
              <button type="button" className="btn btn-link btn-sm p-0" disabled={loading || data.page >= totalPages} onClick={() => load(data.page + 1)}>
                Older ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ChouxboxFileGroupPicker;
